import { Box } from '@mui/material'
import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Appointment, User } from '~/types'

import { Logger } from '~/utils/logging'
import { SessionCallV2 } from './SessionCallV2'
import { SessionHeader } from './SessionHeader'
import { SessionPreCall } from './SessionPreCall'

interface SessionProps {
  appointment: Appointment
  sessionName: string
  token: string
  modal: boolean
  logger: Logger
  me: User
}

interface SessionContext {
  // Misc
  me: User
  // Client State
  appointment: Appointment
  client: any | null
  clientError: boolean
  modal: boolean
  screen: 'precall' | 'call'
  sessionName: string
  token: string
  logger: Logger
  // Hardware State
  activeCamera: any | null
  activeMicrophone: any | null
  activeSpeaker: any | null
  cameras: any[]
  isMuted: boolean
  isAudioOn: boolean
  isVideoOn: boolean
  microphones: any[]
  speakers: any[]
  // DOM Refs
  containerRef: React.RefObject<HTMLDivElement>
  // Methods
  setActiveCameraId: (id: string) => void
  setActiveMicrophoneId: (id: string) => void
  setActiveSpeakerId: (id: string) => void
  setCameras: (cameras: any[]) => void
  setIsMuted: (isMuted: boolean) => void
  setIsAudioOn: (isAudioOn: boolean) => void
  setIsVideoOn: (isVideoOn: boolean) => void
  setMicrophones: (microphones: any[]) => void
  setScreen: (screen: 'precall' | 'call') => void
  setSpeakers: (speakers: any[]) => void
}

const ZoomSessionContext = createContext({} as SessionContext)

export const useZoomSession = (): SessionContext => useContext(ZoomSessionContext)

export const SessionProvider: React.FC<SessionProps> = props => {
  /**
   * Client State
   */
  const [screen, setScreen] = useState<'precall' | 'call'>('precall')
  const [client, setClient] = useState<any | null>(null)
  const [clientError, setClientError] = useState(false)

  /**
   * Hardware State
   */
  const [isMuted, setIsMuted] = useState(false)
  const [isAudioOn, setIsAudioOn] = useState(false)
  const [isVideoOn, setIsVideoOn] = useState(true)
  const [cameras, setCameras] = useState<any[]>([])
  const [activeCameraId, setActiveCameraId] = useState<string | null>(null)
  const activeCamera = cameras.find(c => c.deviceId === activeCameraId) ?? null
  const [microphones, setMicrophones] = useState<any[]>([])
  const [activeMicrophoneId, setActiveMicrophoneId] = useState<string | null>(null)
  const activeMicrophone = microphones.find(c => c.deviceId === activeMicrophoneId) ?? null
  const [speakers, setSpeakers] = useState<any[]>([])
  const [activeSpeakerId, setActiveSpeakerId] = useState<string | null>(null)
  const activeSpeaker = speakers.find(c => c.deviceId === activeSpeakerId) ?? null

  /**
   * DOM Refs
   */
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    try {
      // Zoom SDK is loaded via CDN in <script> tag; typing is not available.
      // @ts-ignore
      setClient(window.WebVideoSDK.default.createClient())
    } catch (e) {
      props.logger.error('Zoom CDN Script has not loaded yet, unable to createClient', e)
      setClientError(true)
    }
  }, [])
  useEffect(() => {
    if (client) {
      client
        ?.init('EN', 'CDN', {
          enforceMultipleVideos: false,
          webEndpoint: 'zoom.us',
          stayAwake: true,
        })
        .catch(e => {
          props.logger.error('Failed to initialize Zoom', e)
          setClientError(true)
        })
    }
  }, [client])

  if (clientError) return <div>Zoom Error</div>

  return (
    <ZoomSessionContext.Provider
      value={{
        // Client State
        me: props.me,
        appointment: props.appointment,
        client,
        clientError,
        modal: props.modal,
        screen,
        sessionName: props.sessionName,
        token: props.token,
        logger: props.logger,
        // Hardware State
        activeCamera,
        activeMicrophone,
        activeSpeaker,
        cameras,
        isMuted,
        isAudioOn,
        isVideoOn,
        microphones,
        speakers,
        // DOM Refs
        containerRef,
        // Methods
        setActiveCameraId,
        setActiveMicrophoneId,
        setActiveSpeakerId,
        setCameras,
        setIsMuted,
        setIsAudioOn,
        setIsVideoOn,
        setMicrophones,
        setScreen,
        setSpeakers,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'white',
        }}
        ref={containerRef}
      >
        <SessionHeader />
        {screen === 'precall' ? <SessionPreCall /> : null}
        {screen === 'call' ? <SessionCallV2 /> : null}
      </Box>
    </ZoomSessionContext.Provider>
  )
}
